import { createBrowserRouter }                   from 'react-router-dom';
import { Link }                                  from 'react-router-dom';
import { RouterProvider as ReactRouterProvider } from 'react-router-dom';
import AuthLayout                                from '@widesk-ui/layouts/AuthLayout';
import { useAuth }                               from '@widesk-ui/hooks/useAuth';
import LoginPage                                 from '@widesk-ui/pages/LoginPage';
import Layout                                    from '@widesk-ui/layouts/Layout';
import LayoutSider                               from '@/components/LayoutSider';
import LayoutHeader                              from '@/components/LayoutHeader';
import BreadcrumbBar                             from '@widesk-ui/components/BreadcrumbBar';
import ErrorPage                                 from '@widesk-ui/pages/ErrorPage';
import View                                      from '@widesk-ui/components/View';
import Spin                                      from '@widesk-ui/components/Spin';
import Button                                    from '@widesk-ui/components/Button';
import Title                                     from '@widesk-ui/components/Title';
import useTheme                                  from '@widesk-ui/hooks/useTheme';
import { Observer }                              from 'mobx-react-lite';
import { observer }                              from 'mobx-react-lite';
import appStore                                  from './stores/appStore';
import breadcrumbBarStore                        from '@widesk-ui/stores/breadcrumbBarStore';
import ResetPasswordPage                         from '@widesk-ui/pages/ResetPasswordPage';
import UserResetPasswordModel                    from '@models/fondations/UserResetPasswordModel';
import { getLocale }                             from '@widesk-core/locale';

// On charge tous les fichiers "Route" du projet
const modules = import.meta.glob([
	'./routes/*Route.ts',
	'./routes/*Route.tsx',
	'./routes/services/*/*Route.ts',
	'./routes/services/*/*Route.tsx',
	'./routes/services/*/*/*Route.ts',
	'./routes/services/*/*/*Route.tsx',
], { eager: true });

const header = (
	<View gap="xs" center row>
		<img alt="logo" src="/favicon.svg" width={30} />
		<Title center align="center" size={32}>Gestion du site public</Title>
	</View>
);

const publicRouter = createBrowserRouter([
	{
		element: <Layout />,
		children: [
			{
				path: '*',
				element: (
					<LoginPage
						footer={<View align="center"><Link to="/reset_password">Mot de passe oublié</Link></View>}
						header={header}
					/>
				),
			},
			{
				path: '/reset_password',
				element: (
					<ResetPasswordPage
						header={header}
						onSubmit={async username => {
							const serviceUrl = window.APP_CONFIG.services.find(s => s.id === 'myaccount')?.url;
							const locale = getLocale();
							const urlSuffix = locale === 'fr'
								? 'fr/changer-mot-de-passe'
								: 'en/confirmation-reset-password';

							const resetPassword = new UserResetPasswordModel();
							await resetPassword.patch({
								email: username,
								routeUrl: serviceUrl + urlSuffix,
							});
						}}
					/>
				),
			},
		],
	},
]);

const routes = Object.values(modules).flatMap((module: any) => module.default);

breadcrumbBarStore.setRoutes(routes);

const privateRouter = createBrowserRouter([
	{
		element: (
			<Observer render={() => (
				<AuthLayout
					fullWidthHeader
					renderHeader={() => <LayoutHeader />}
					renderSider={collapsedState => <LayoutSider collapsedState={collapsedState} />}
					renderTopPage={() => <BreadcrumbBar />}
					saveCollapsed
					initialCollapsed={false}
					siderCollapsedWidth={appStore.isAppMenuOpened ? 106 : 54}
					disableTrigger
					siderWidth={appStore.isAppMenuOpened ? 304 : 54}
				/>
			)} />
		),
		children: routes,
		errorElement: <ErrorPage />,
	},
]);

const RouterProvider = observer(function () {
	const { token, isLogging } = useAuth();

	return (
		<>
			{token ? (
				isLogging ? <LoadingPage /> : <ReactRouterProvider router={privateRouter} />
			) : (
				<ReactRouterProvider router={publicRouter} />
			)}
		</>
	);
});

export default RouterProvider;

export const LoadingPage = () => {
	const { logout } = useAuth();
	const theme = useTheme();

	return (
		<View height="100vh" center gap={20} bg={theme.colorBgContainer} color={theme.colorTextBase}>
			<Spin size="large" tip="Chargement de l'application" />

			<Button type="link" onClick={() => logout()}>Déconnexion</Button>
		</View>
	);
};
